import React, {useCallback, useEffect, useMemo, useState} from "react";
import classnames from "classnames";
import {UserHomepageColorThemeContext} from "../../../index";
import {DefaultPhoneUserHomepageColorThemeContextValue, DefaultWebUserHomepageColorThemeContextValue, UserHomePageTypes} from "../../../models";
import {hexToRGB} from "../../../../../../core/services/utils/utils";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import Api from "../../../../../../core/services/api/api";
import usePartnerColorTheme from "../../../../../hooks/use-partner-color-theme";
import {usePartnerInformation} from "../../../../../hooks/use-partner-information";
import {TinyColor} from '@ctrl/tinycolor';
import PartnerIndustryRules from '../../../../../../core/services/partner-industry-rules';

const mixWhite = (hex, amount) => new TinyColor(hex).mix('#ffffff', amount).toHexString();
const mixBlack = (hex, amount) => new TinyColor(hex).mix('#000000', amount).toHexString();

/** Matches client `$app-primary-color-2` — a darker header surface than the base accent. */
const HEADER_BACKGROUND_BLACK_MIX = 35;
const HEADER_BADGE_BLACK_MIX = 42;
const HEADER_DEEP_BLACK_MIX = 50;

/**
 * Maps partner primary / secondary / tertiary palette colors to web homepage CSS variables.
 *
 * @param {{
 *   primary: string,
 *   secondary: string,
 *   tertiary?: string,
 *   preferSecondaryForProductHeaders?: boolean,
 * }} palette
 * @return {Record<string, string>}
 */
const buildWebHomepageThemeFromPartnerPalette = ({
                                                     primary,
                                                     secondary,
                                                     tertiary,
                                                     preferSecondaryForProductHeaders = false,
                                                 }) => {
    const tertiaryColor = tertiary || secondary;
    const headerAccent = preferSecondaryForProductHeaders ? secondary : primary;
    const headerBackground = mixBlack(headerAccent, HEADER_BACKGROUND_BLACK_MIX);

    return {
        primaryColor: primary,
        primaryColor2: mixWhite(primary, 15),
        primaryColor3: mixWhite(primary, 35),
        primaryColor4: mixWhite(primary, 55),
        primaryColor5: mixWhite(primary, 65),
        primaryColor6: mixWhite(primary, 85),
        mainPageHeaderRed: mixWhite(primary, 92),
        secondaryColor: secondary,
        secondaryColor2: mixWhite(secondary, 25),
        secondaryColor3: mixWhite(secondary, 50),
        secondaryColor4: '#FFFFFF',
        secondaryColor5: mixWhite(secondary, 70),
        productHeaderBlue: headerBackground,
        newBlueColor: mixBlack(headerAccent, HEADER_BADGE_BLACK_MIX),
        newDarkBlue: mixBlack(headerAccent, HEADER_DEEP_BLACK_MIX),
        lighterBlue: mixWhite(headerAccent, 92),
        transparentBlue: headerAccent,
        showMoreBackground: '#F5F7FA',
        whiteColor: '#FFFFFF',
        loadingBackground: '#F1F2F5',
        primaryTextColor: '#404042',
        secondaryTextColor: '#858585',
        disabledTextColor: '#000000',
        hintTextColor: '#000000',
        black: '#000000',
        productInformationBoxShadow: '#000000',
        largeProductBodyBorderColor: headerAccent,
        tagsContainerBackgroundColor: mixWhite(headerAccent, 82),
        grey2: new TinyColor(tertiaryColor).darken(15).toHexString(),
        grey6: new TinyColor(tertiaryColor).toHexString(),
    };
};


/**
 * Fetches the default color theme of the application based on the given viewType.
 *
 * @param {UserHomePageTypes} viewType
 * @return {any}
 */
const getDefaultTheme = (viewType) => {
    switch (viewType) {
        case UserHomePageTypes.web:
            return DefaultWebUserHomepageColorThemeContextValue;
        case UserHomePageTypes.phone:
        default:
            return DefaultPhoneUserHomepageColorThemeContextValue;
    }
}

const applyWebPreviewLightModeDefaults = (theme) => ({
    ...theme,
    secondaryColor4: '#FFFFFF',
    whiteColor: '#FFFFFF',
    primaryTextColor: '#404042',
    secondaryTextColor: '#858585',
});

const UserHomepageColorTheme = ({viewType, children}) => {
    const [theme, setTheme] = useState(getDefaultTheme(viewType));
    const isMounted = useIsMounted();
    const {partnerColorTheme} = usePartnerColorTheme({autoFetch: true});
    const partnerInformation = usePartnerInformation();

    const themeStyle = useMemo(() => ({
        ...Object.fromEntries(Object
            .entries(theme)
            .filter(([_, value]) => (hexToRGB(value)?.length ?? 0) === 3)
            .map(([name, value]) => [`--${name}`, hexToRGB(value).reduce((p, c, i, array) => p.concat(`${c}${i < (array.length - 1) ? `, ` : ''}`), '')])
        ),
        colorScheme: 'light',
    }), [theme])

    const getHomepageColorTheme = useCallback(async () => {
        const response = await Api.getDefaultColorTheme()
        if (!isMounted())
            return;

        let themeObject = {};
        if (response?.resultFlag) {
            themeObject = Object.fromEntries(response?.data?.values?.map(e => [e.colorName, e.value]) ?? []);
        }

        let mergedTheme = {
            ...getDefaultTheme(viewType),
            ...themeObject,
        };

        if (viewType === UserHomePageTypes.web) {
            const palette = partnerColorTheme?.light?.palette;
            const partnerPrimary = palette?.primary?.main;
            const partnerSecondary = palette?.secondary?.main;
            const partnerTertiary = palette?.tertiary?.main;

            if (partnerPrimary && partnerSecondary) {
                mergedTheme = {
                    ...mergedTheme,
                    ...buildWebHomepageThemeFromPartnerPalette({
                        primary: partnerPrimary,
                        secondary: partnerSecondary,
                        tertiary: partnerTertiary,
                        preferSecondaryForProductHeaders: PartnerIndustryRules.preferSecondaryForProductHeaders(partnerInformation?.id),
                    }),
                    ...themeObject,
                };
            }

            mergedTheme = applyWebPreviewLightModeDefaults(mergedTheme);
        }

        setTheme(mergedTheme);
    }, [isMounted, partnerColorTheme, partnerInformation?.id, viewType]);

    useEffect(() => {
        getHomepageColorTheme().then();
    }, [getHomepageColorTheme])

    return (
        <>
            <UserHomepageColorThemeContext.Provider value={theme}>
                <div
                    className={classnames({
                        'user-web-homepage-color-theme': viewType === UserHomePageTypes.web,
                    })}
                    style={themeStyle}
                >
                    {children}
                </div>
            </UserHomepageColorThemeContext.Provider>
        </>
    )
}

export default UserHomepageColorTheme;
